import {
  createContext,
  FC,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type InputCallback = (input: HTMLInputElement) => void;

type ValidationContextType = {
  isInputsValid: boolean;
  report: InputCallback;
  unregister: InputCallback;
};

// Outside a ValidationProvider (e.g. inputs on detail pages) nothing is tracked.
const ValidationContext = createContext<ValidationContextType>({
  isInputsValid: true,
  report: () => {},
  unregister: () => {},
});

// Valid when every mounted input tracked via useInputValidity passes native constraint
// validation (required, pattern, min/max, type). SlideOverWrapper provides one per opened
// slide-over, so only its own inputs gate its submit.
export const ValidationProvider: FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isInputsValid, setIsInputsValid] = useState(true);
  // A ref, not state: an input changing state must not re-render the provider by itself.
  const invalidInputs = useRef(new Set<HTMLInputElement>());

  // checkValidity, not reportValidity: no native bubble (and no focus jump) while typing;
  // invalid inputs are marked by their :invalid styling only.
  // Each input checks only itself; setting an unchanged boolean doesn't re-render anything.
  const report = useCallback<InputCallback>((input) => {
    if (input.checkValidity()) invalidInputs.current.delete(input);
    else invalidInputs.current.add(input);
    setIsInputsValid(!invalidInputs.current.size);
  }, []);

  const unregister = useCallback<InputCallback>((input) => {
    invalidInputs.current.delete(input);
    setIsInputsValid(!invalidInputs.current.size);
  }, []);

  const value = useMemo(
    () => ({ isInputsValid, report, unregister }),
    [isInputsValid, report, unregister],
  );

  return (
    <ValidationContext.Provider value={value}>
      {children}
    </ValidationContext.Provider>
  );
};

// True when no input inside the nearest ValidationProvider is invalid.
export const useInputsValid = () => useContext(ValidationContext).isInputsValid;

// Tracks the input from mount to unmount, so a freshly mounted input counts the same as an edited one.
// The effect has no dependency list on purpose: after every render the input re-checks itself, so
// changes to the value or to constraints (pattern, required, disabled, type) are picked up after the
// DOM update. When the ref target goes away (e.g. TextInputAtom switching to an untracked textarea),
// the old input is unregistered.
export const useInputValidity = (
  inputRef: RefObject<HTMLInputElement | null>,
) => {
  const { report, unregister } = useContext(ValidationContext);
  const registered = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const input = inputRef.current;
    if (registered.current && registered.current !== input)
      unregister(registered.current);
    registered.current = input;
    if (input) report(input);
  });

  useEffect(
    () => () => {
      if (registered.current) unregister(registered.current);
    },
    [unregister],
  );
};
